import {
    AmbientLight,
    Clock,
    Color,
    DirectionalLight,
    Mesh,
    PCFSoftShadowMap,
    PerspectiveCamera,
    PointLight,
    Raycaster,
    Scene,
    sRGBEncoding,
    Vector2,
    WebGLRenderer,
} from "three";
import { OrbitControls } from "../assets/JS/OrbitControls";
import { IRoomSize } from "./interfaces";

import { EffectComposer } from "assets/JS/EffectComposer";
import { RenderPass } from "assets/JS/RenderPass";
import { OutlinePass } from "assets/JS/OutlinePass";

import { Robot } from "./Robot";
import { StaticMesh } from "./StaticMesh";
import { onMouseDown, onMouseUp, onMouseMove, onStartDrag, onArmHover } from "./Interactions";
import { selectionEvents } from "./Events";
import { SphereHelper, TransformHelper } from "./Helpers";

export class GraphicService {
    static divRef: HTMLDivElement;
    static scene: Scene;
    static camera: PerspectiveCamera;
    static renderer: WebGLRenderer;

    static composer: EffectComposer;
    static outlineEffect: OutlinePass;
    static control: OrbitControls;
    static castableMeshes: Mesh[] = [];
    static armMeshes: Mesh[] = [];
    static raycaster = new Raycaster();
    static outlineColor: Color;
    static ambLight: AmbientLight;
    static mainLight: PointLight;
    static spotLight: DirectionalLight;
    static transformHelper: TransformHelper;
    static sphereHelper = new SphereHelper();
    private static isShadow = false;
    private static renderPass: RenderPass;

    static init(
        divRef: HTMLDivElement,
        roomSize: IRoomSize,
        outlineHexColor = "#00FF00",
        onAnimate: () => void = () => {
            const delta = new Clock().getDelta();
            if (delta > 50) {
                GraphicService.enableShadows(false);
            }
            GraphicService.composer.render(delta);
        }
    ): void {
        GraphicService.divRef = divRef;
        GraphicService.scene = new Scene();
        const width = GraphicService.divRef.clientWidth;
        const height = GraphicService.divRef.clientHeight;
        const isMobile = width <= 960;
        const fov = isMobile ? 90 : 75;
        GraphicService.camera = new PerspectiveCamera(fov, width / height, 0.001, 100);
        GraphicService.camera.position.x = isMobile ? -2 : -1;
        GraphicService.camera.position.z = isMobile ? 2 : 1;
        GraphicService.camera.position.y = 3;

        GraphicService.renderer = new WebGLRenderer({
            antialias: true,
            alpha: true,
            powerPreference: "high-performance",
            preserveDrawingBuffer: true,
            logarithmicDepthBuffer: true,
        });

        GraphicService.renderer.setSize(width, height);
        GraphicService.renderer.setClearColor(0x000000, 0.0);
        GraphicService.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));
        GraphicService.renderer.outputEncoding = sRGBEncoding;
        GraphicService.renderer.shadowMap.enabled = GraphicService.isShadow;
        GraphicService.renderer.shadowMap.type = PCFSoftShadowMap;

        GraphicService.divRef.appendChild(GraphicService.renderer.domElement);

        GraphicService.outlineColor = new Color(outlineHexColor);

        GraphicService.control = new OrbitControls(
            GraphicService.camera,
            GraphicService.renderer.domElement
        );

        GraphicService.control.maxPolarAngle = (9 * Math.PI) / 20;

        GraphicService.raycaster = new Raycaster();

        GraphicService.scene.add(GraphicService.sphereHelper);

        GraphicService.setupLighting(roomSize);
        GraphicService.setupEffects();

        GraphicService.renderer.setAnimationLoop(onAnimate);

        window.addEventListener("resize", GraphicService.onWindowResize, false);

        GraphicService.divRef.addEventListener("pointerdown", onMouseDown);
        GraphicService.divRef.addEventListener("pointerup", onMouseUp);
        GraphicService.divRef.addEventListener("pointermove", onMouseMove);
        GraphicService.divRef.addEventListener("pointermove", onArmHover);

        selectionEvents.on("startDrag", (id: string) => {
            onStartDrag(id);
        });
    }

    private static setupEffects() {
        const resolution = new Vector2(
            GraphicService.divRef.clientWidth,
            GraphicService.divRef.clientHeight
        );

        GraphicService.renderPass = new RenderPass(GraphicService.scene, GraphicService.camera);

        GraphicService.outlineEffect = new OutlinePass(
            resolution,
            GraphicService.scene,
            GraphicService.camera
        );

        GraphicService.outlineEffect.visibleEdgeColor = GraphicService.outlineColor;
        GraphicService.outlineEffect.hiddenEdgeColor = GraphicService.outlineColor;
        GraphicService.outlineEffect.edgeGlow = 0.8;
        GraphicService.outlineEffect.edgeStrength = 5;
        GraphicService.outlineEffect.edgeThickness = 1.5;

        GraphicService.composer = new EffectComposer(GraphicService.renderer);
        GraphicService.composer.addPass(GraphicService.renderPass);
        GraphicService.composer.addPass(GraphicService.outlineEffect);
    }

    private static setupLighting(roomSize: IRoomSize) {
        GraphicService.ambLight = new AmbientLight("#BDBDBD", 0.75);
        GraphicService.scene.add(GraphicService.ambLight);

        GraphicService.mainLight = new PointLight("#ffffff", 0.55);
        GraphicService.mainLight.position.y = 0.85 * roomSize.height;
        GraphicService.mainLight.decay = 0.83;

        GraphicService.mainLight.distance = Math.max(roomSize.width, roomSize.length) * 2;

        GraphicService.mainLight.castShadow = false;

        GraphicService.scene.add(GraphicService.mainLight);

        GraphicService.spotLight = new DirectionalLight("#ffffff", 0.3);

        GraphicService.spotLight.position.y = 2.5;
        GraphicService.spotLight.position.x = 0.0;
        GraphicService.spotLight.position.z = -0.2;
        GraphicService.spotLight.lookAt(0.0, 0.0, 0.0);

        GraphicService.spotLight.castShadow = GraphicService.isShadow;
        GraphicService.spotLight.shadow.mapSize.width = 1024;
        GraphicService.spotLight.shadow.mapSize.height = 1024;
        GraphicService.spotLight.shadow.camera.near = 0.1;
        GraphicService.spotLight.shadow.camera.far = 20;

        GraphicService.scene.add(GraphicService.spotLight);
    }

    static enableShadows(shadow: boolean): void {
        GraphicService.isShadow = shadow;
        GraphicService.spotLight.castShadow = shadow;
        GraphicService.renderer.shadowMap.enabled = shadow;
    }

    static updateLighting(roomSize: IRoomSize): void {
        GraphicService.mainLight.position.y = 0.85 * roomSize.height;
        GraphicService.mainLight.distance = Math.max(roomSize.width, roomSize.length) * 2;
    }

    static onWindowResize(): void {
        if (GraphicService.divRef) {
            const width = GraphicService.divRef.clientWidth;
            const height = GraphicService.divRef.clientHeight;

            GraphicService.camera.aspect = width / height;
            GraphicService.camera.updateProjectionMatrix();

            GraphicService.renderer.setSize(width, height);
            GraphicService.composer.setSize(width, height);
        }
    }

    static highlightObject(
        obj: Robot | StaticMesh | { SM_Patient: StaticMesh; SM_Bed: StaticMesh },
        hexColor = "#00FF00"
    ): boolean {
        GraphicService.outlineEffect.visibleEdgeColor = new Color(hexColor);
        GraphicService.outlineEffect.hiddenEdgeColor = new Color(hexColor);
        GraphicService.outlineEffect.selectedObjects = [];
        return GraphicService.addToHighlight(obj);
    }

    static clearHighlight(): void {
        GraphicService.outlineEffect.selectedObjects = [];
    }

    static addToHighlight(
        obj: Robot | StaticMesh | { SM_Patient: StaticMesh; SM_Bed: StaticMesh }
    ): boolean {
        if (obj instanceof Robot) {
            GraphicService.outlineEffect.selectedObjects = [obj.baseMesh, obj.armMesh];
            return true;
        }

        if (obj instanceof StaticMesh) {
            GraphicService.outlineEffect.selectedObjects = obj.meshes;
            return true;
        }

        if (obj as { SM_Patient: StaticMesh; SM_Bed: StaticMesh }) {
            GraphicService.outlineEffect.selectedObjects = obj.SM_Bed.meshes.concat(
                obj.SM_Patient.meshes
            );
            return true;
        }

        return false;
    }

    static captureScene(maxWidth = 450): string {
        const renderer = GraphicService.renderer;
        const canvas = renderer.domElement;

        const width = canvas.width;
        const height = canvas.height;

        const scale = maxWidth / width;

        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = width * scale;
        tempCanvas.height = height * scale;

        const ctx = tempCanvas.getContext("2d");

        if (!ctx) return "";

        ctx.drawImage(canvas, 0, 0, tempCanvas.width, tempCanvas.height);

        return tempCanvas.toDataURL("image/jpeg", 0.8);
    }

    static async captureSceneBlob(scaleMultiplier = 2): Promise<Blob | null> {
        const renderer = GraphicService.renderer;
        const canvas = renderer.domElement;

        const width = canvas.width;
        const height = canvas.height;

        const tempCanvas = document.createElement("canvas");

        // upscale for higher resolution
        tempCanvas.width = width * scaleMultiplier;
        tempCanvas.height = height * scaleMultiplier;

        const ctx = tempCanvas.getContext("2d");
        if (!ctx) return null;

        // draw scaled image
        ctx.drawImage(canvas, 0, 0, tempCanvas.width, tempCanvas.height);

        return new Promise((resolve) => {
            tempCanvas.toBlob((blob) => {
                resolve(blob);
            }, "image/png");
        });
    }

    static dispose(): void {
        // Stop the render loop
        GraphicService.renderer.setAnimationLoop(null);

        // Remove window event listener
        window.removeEventListener("resize", GraphicService.onWindowResize);

        // Remove pointer event listeners
        if (GraphicService.divRef) {
            GraphicService.divRef.removeEventListener("pointerdown", onMouseDown);
            GraphicService.divRef.removeEventListener("pointerup", onMouseUp);
            GraphicService.divRef.removeEventListener("pointermove", onMouseMove);
            GraphicService.divRef.removeEventListener("pointermove", onArmHover);

            // Remove the canvas from the DOM
            if (GraphicService.renderer.domElement.parentNode === GraphicService.divRef) {
                GraphicService.divRef.removeChild(GraphicService.renderer.domElement);
            }
        }

        // Dispose Three.js resources
        GraphicService.renderer.dispose();
        GraphicService.control.dispose();

        // Clear scene
        GraphicService.scene.clear();

        // Clear mesh arrays
        GraphicService.castableMeshes = [];
        GraphicService.armMeshes = [];

        // Null out statics so they don't hold references
        GraphicService.divRef = null!;
        GraphicService.scene = null!;
        GraphicService.camera = null!;
        GraphicService.renderer = null!;
        GraphicService.composer = null!;
        GraphicService.outlineEffect = null!;
        GraphicService.control = null!;
    }
}
